import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import dayJS from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { RangeTime } from '@wm-front/shared/interfaces'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { getDateTimeFromSelectValue } from '@wm-front/shared/utils'

dayJS.extend(isBetween)

function patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) {
      return null
    }

    const valid = regex.test(control.value)

    return valid ? null : error
  }
}

/** Used to know if whether or not the one field at least has been filled
 * @param {ValidatorFn} validator
 * @param {string[]} controlNames the fields who we wants test
 * @return {ValidatorFn}
 */
function oneAtLeast(
  validator: ValidatorFn,
  controlNames: string[]
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const hasAtLeastOne = controlNames.some((k) => {
      return group.get(k) !== null
        ? !validator(group.get(k) as AbstractControl)
        : false
    })

    return hasAtLeastOne ? null : { atLeastOne: true }
  }
}

/** Check whether or not a date is before another one */
export function dateLessThan(from: string, to: string) {
  return (group: AbstractControl): { [key: string]: unknown } => {
    const f = group.get(from)
    const t = group.get(to)

    if (f && t && f.value >= t.value) {
      return {
        dates: 'Date from should be less than Date To',
      }
    }

    return {}
  }
}

/** Used to know if time range is inside a list */
export function isAvailableRangeTime(
  start: string,
  end: string,
  list: RangeTime[],
  date: Date,
  startHour: number,
  error: ValidationErrors
) {
  return (group: AbstractControl): { [key: string]: unknown } | null => {
    let isInRangeTime = false
    const startControl = group.get(start)
    const endControl = group.get(end)

    if (startControl && endControl) {
      const startDate = getDateTimeFromSelectValue(
        startControl.value,
        startHour,
        date
      )
      const endDate = getDateTimeFromSelectValue(
        endControl.value,
        startHour,
        date
      )

      list.forEach((item) => {
        if (
          dayJS(startDate).isBetween(
            item.dateStart,
            item.dateEnd,
            'millisecond',
            '[)'
          ) ||
          dayJS(endDate).isBetween(
            item.dateStart,
            item.dateEnd,
            'millisecond',
            '(]'
          ) ||
          dayJS(item.dateStart).isBetween(
            startDate,
            endDate,
            'millisecond',
            '[)'
          ) ||
          dayJS(item.dateEnd).isBetween(startDate, endDate, 'millisecond', '(]')
        ) {
          isInRangeTime = true
        }
      })
    }

    return isInRangeTime ? error : null
  }
}

/** Used to know if whether or not the phone number is valid
 * @param {CountryCode} defaultCountry
 * @return {ValidatorFn}
 */
function phoneNumberIsValid(defaultCountry: CountryCode): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }

    return parsePhoneNumber(control.value, defaultCountry)?.isValid()
      ? null
      : { phoneNumberInvalid: true }
  }
}

const customValidators = {
  patternValidator,
  oneAtLeast,
  dateLessThan,
  isAvailableRangeTime,
  phoneNumberIsValid,
}

export default customValidators
